import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSendbirdStateContext } from '@sendbird/uikit-react/useSendbirdStateContext';
import {
  GroupChannelFilter,
  GroupChannelCollection,
  GroupChannelCollectionEventHandler,
} from '@sendbird/chat/groupChannel';
import type { BaseChannel } from '@sendbird/chat';
import { MutableRef } from 'effect';

import { setUnreadFromChannelsAction } from '@/domain/chat';
import { Apps } from '@/domain/env/helpers';
import { computeUnreadCounters, UnreadCountableChannel } from '@/components/mui/Layouts/components/Chat/helpers';

/**
 * Chat v2 unread counters — the live, WS-driven source.
 *
 * While the chat drawer is open the Sendbird WS is connected (ChatProvider owns
 * the connection). This hook opens a single `GroupChannelCollection` over the
 * user's AP channels and keeps the three filter counters (All / current company /
 * current document) in Redux in sync with it.
 *
 * Trigger: the collection's `onChannelsAdded/Updated/Deleted` callbacks fire from
 * Sendbird's native WS push on every send and read — no backend WS, no polling.
 * On each fire we recompute ALL counters from the full `collection.channels` list
 * (see computeUnreadCounters). Recomputation is idempotent, so the SDK's multiple
 * rapid fires per change all converge on the correct absolute totals; there is no
 * delta arithmetic that could drift (this replaces the deprecated backend-WS
 * `chatUnreadUpdateFromWsAction` readDelta path).
 *
 * When the drawer closes the WS is torn down and this hook unmounts (dispose()).
 * The closed-state counters come from the REST `sendbird/unread` endpoint seeded
 * by the chatNotifications saga — this hook only owns the open/live window.
 */
const useChatUnreadCounters = (): void => {
  const dispatch = useDispatch();
  const sdk = useSendbirdStateContext().stores.sdkStore.sdk;

  useEffect(() => {
    if (!sdk?.groupChannel || !sdk.currentUser) return undefined;

    const filter = new GroupChannelFilter();
    filter.includeEmpty = true;
    // Context (companyId/documentId) lives in metadata; the counters read it via
    // resolveChannelContext(cachedMetaData), so metadata must be loaded.
    filter.includeMetaData = true;
    // Counters are AP-scoped, matching the channels the chat surfaces (Apps.AP).
    filter.customTypesFilter = [Apps.AP];

    const collection: GroupChannelCollection = sdk.groupChannel.createGroupChannelCollection({ filter, limit: 100 });
    // Closure state held in Effect MutableRef cells (project style forbids `let`, and
    // the effect body can't reassign a binding): `disposed` guards async work after
    // unmount; `scheduled` coalesces the SDK's burst of per-message fires into one
    // recompute per tick.
    const disposed = MutableRef.make(false);
    const scheduled = MutableRef.make(false);

    const recompute = (): void => {
      if (MutableRef.get(disposed)) return;
      const counters = computeUnreadCounters(collection.channels as unknown as ReadonlyArray<UnreadCountableChannel>);
      dispatch(setUnreadFromChannelsAction(counters));
    };

    const scheduleRecompute = (): void => {
      if (MutableRef.get(scheduled)) return;
      MutableRef.set(scheduled, true);
      // Microtask: collapse the multiple onChannelsUpdated fires Sendbird emits for
      // a single send/read into one dispatch, while staying synchronous-ish.
      Promise.resolve().then(() => {
        MutableRef.set(scheduled, false);
        recompute();
      });
    };

    const handler: GroupChannelCollectionEventHandler = {
      onChannelsAdded: (_ctx, _channels: BaseChannel[]) => scheduleRecompute(),
      onChannelsUpdated: (_ctx, _channels: BaseChannel[]) => scheduleRecompute(),
      onChannelsDeleted: (_ctx, _urls: string[]) => scheduleRecompute(),
    };
    collection.setGroupChannelCollectionHandler(handler);

    // Load every page so the "All" total and per-company sums cover all channels,
    // not just the first page. Recompute after each page so the badge fills in
    // progressively on large accounts instead of waiting for the full load. Recursion
    // (not a `while` loop) drains the pages, respecting the project's no-loop style.
    const loadRemaining = async (): Promise<void> => {
      if (MutableRef.get(disposed) || !collection.hasMore) return;
      await collection.loadMore();
      recompute();
      await loadRemaining();
    };

    const loadAll = async (): Promise<void> => {
      try {
        // Prime with whatever the initial fetch already cached, then drain the rest.
        recompute();
        await loadRemaining();
      } catch (e) {
        console.error('[useChatUnreadCounters] Failed to load channels for counters:', e);
      }
    };
    loadAll();

    return () => {
      MutableRef.set(disposed, true);
      collection.dispose();
    };
  }, [sdk, dispatch]);
};

export default useChatUnreadCounters;
